"use client";

import { createClient } from "@/lib/supabase/client";
import type { FeaturedBanner, Tables } from "@/types/database";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Type definitions
type Tag = Tables<"tags">;
type Publisher = Tables<"publishers">;

// Query keys for consistent caching
export const metadataKeys = {
  all: ["metadata"] as const,
  tags: () => [...metadataKeys.all, "tags"] as const,
  publishers: () => [...metadataKeys.all, "publishers"] as const,
  featuredBanners: () => [...metadataKeys.all, "featured-banners"] as const,
  gameMetadata: (gameId: number) => [...metadataKeys.all, "game", gameId] as const,
};

// Helper function to fetch tags
async function fetchTags(supabase: ReturnType<typeof createClient>): Promise<Tag[]> {
  const { data, error } = await supabase
    .from("tags")
    .select("*")
    .order("display_order", { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch tags: ${error.message}`);
  }

  return data || [];
}

// Fetch all tags
export function useTags() {
  const supabase = createClient();

  return useQuery({
    queryKey: metadataKeys.tags(),
    queryFn: async (): Promise<Tag[]> => {
      return fetchTags(supabase);
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Fetch all publishers
export function usePublishers() {
  const supabase = createClient();

  return useQuery({
    queryKey: metadataKeys.publishers(),
    queryFn: async (): Promise<Publisher[]> => {
      const { data, error } = await supabase
        .from("publishers")
        .select("*")
        .order("name", { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch publishers: ${error.message}`);
      }

      return data || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Fetch all metadata in a single hook (useful for forms)
export function useAllMetadata() {
  const tags = useTags();
  const publishers = usePublishers();

  return {
    tags: tags.data || [],
    publishers: publishers.data || [],
    isLoading: tags.isLoading || publishers.isLoading,
    error: tags.error || publishers.error,
  };
}

// Fetch featured banners
export function useFeaturedBanners(options?: { enabled?: boolean }) {
  const supabase = createClient();

  return useQuery({
    queryKey: metadataKeys.featuredBanners(),
    queryFn: async (): Promise<FeaturedBanner[]> => {
      const { data, error } = await supabase
        .from("featured_banners")
        .select("*")
        .eq("active", true)
        .order("display_order", { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch featured banners: ${error.message}`);
      }

      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - banners might change more frequently
    enabled: options?.enabled !== false,
  });
}

// Fetch game's associated metadata (for editing) - updated for multiple publishers
export function useGameMetadata(gameId: number) {
  const supabase = createClient();

  return useQuery({
    queryKey: metadataKeys.gameMetadata(gameId),
    queryFn: async () => {
      const [tagsResult, publishersResult] = await Promise.all([
        supabase.from("game_tags").select("tag_id").eq("game_id", gameId),
        supabase.from("game_publishers").select("publisher_id").eq("game_id", gameId),
      ]);

      if (tagsResult.error) {
        throw new Error(`Failed to fetch game tags: ${tagsResult.error.message}`);
      }
      if (publishersResult.error) {
        throw new Error(`Failed to fetch game publishers: ${publishersResult.error.message}`);
      }

      return {
        tagIds: tagsResult.data?.map((t: { tag_id: number }) => t.tag_id) || [],
        publisherIds:
          publishersResult.data?.map((p: { publisher_id: number }) => p.publisher_id) || [],
      };
    },
    enabled: !!gameId,
  });
}

// Fetch user publishers for current user
export function useUserPublishers(userId?: string) {
  const supabase = createClient();

  return useQuery({
    queryKey: ["userPublishers", userId],
    queryFn: async () => {
      let currentUserId = userId;
      if (!currentUserId) {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return [];
        currentUserId = user.id;
      }

      const { data, error } = await supabase
        .from("user_publishers")
        .select(`
          publisher:publishers(id, name, slug)
        `)
        .eq("user_id", currentUserId);

      if (error) {
        throw new Error(`Failed to fetch user publishers: ${error.message}`);
      }

      return data?.map((up) => up.publisher).filter(Boolean) || [];
    },
    enabled: true,
  });
}

// Mutation: Create/Update featured banner
export function useUpsertFeaturedBanner() {
  const queryClient = useQueryClient();
  const supabase = createClient();

  return useMutation({
    mutationFn: async (
      banner: Omit<FeaturedBanner, "created_at" | "updated_at"> & { id?: string }
    ) => {
      const bannerData = {
        ...banner,
        updated_at: new Date().toISOString(),
      };

      if (banner.id) {
        // Update existing banner
        const { data, error } = await supabase
          .from("featured_banners")
          .update(bannerData)
          .eq("id", banner.id)
          .select()
          .single();

        if (error) {
          throw new Error(`Failed to update featured banner: ${error.message}`);
        }

        return data;
      }
      // Create new banner
      const { data, error } = await supabase
        .from("featured_banners")
        .insert({ ...bannerData, created_at: new Date().toISOString() })
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to create featured banner: ${error.message}`);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: metadataKeys.featuredBanners() });
    },
  });
}

// Mutation: Delete featured banner
export function useDeleteFeaturedBanner() {
  const queryClient = useQueryClient();
  const supabase = createClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("featured_banners").delete().eq("id", id);

      if (error) {
        throw new Error(`Failed to delete featured banner: ${error.message}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: metadataKeys.featuredBanners() });
    },
  });
}
